import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { RemoveDormatService } from './remove-dormat.service';
import { safeJsonInput } from '../../../shared/_utils/safe-json-input';

@Component({
  selector: 'gtw-remove-dormat',
  templateUrl: './remove-dormat.component.html',
  styleUrls: ['./remove-dormat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class RemoveDormatComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;

  // Component data
  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  gridData: any[] = [];

  // Component properties
  entities: any[] = [];
  btnDisabled = false;
  crudLoading = false;
  cc_keys: any[] = [];

  // Confirmation modal configuration
  showConfirmModal = true;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to remove the dormat for all selected batch?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orangeDark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast'
  };

  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    const urls = safeJsonInput<any>(baseUrls, null, 'remove-dormat.base-urls');
    if (urls && Object.keys(urls).length > 0) {
      this.baseURLs = urls;
      this.tryInit();
    }
  }

  @Input('filter-params')
  set _filter_params(filterParams: string) {
    const params = safeJsonInput<any>(filterParams, null, 'remove-dormat.filter-params');
    if (params && Object.keys(params).length > 0) {
      this.filterParams = params;
      this.tryInit();
    }
  }

  @Input('header-data')
  set headerData(headerData: any) {
    const parsed = safeJsonInput<any>(headerData, null, 'remove-dormat.header-data');
    if (parsed) {
      this.globalService = parsed;
      this.GENERAL_CONFIG = this.globalService.generalConfigData;
      this.tryInit();
    }
  }

  @Input('row-data')
  set _rowData(rowData: string) {
    const parsed = safeJsonInput<any>(rowData, null, 'remove-dormat.row-data');
    if (parsed) {
      this.rowData = parsed;
      this.tryInit();
    }
  }

  @Input('grid-data')
  set _gridData(gridData: string) {
    // Typed as `any`: the grid sends an array for batch actions and a
    // `{rowData, colData}` object for the pencil click; init() handles both.
    const parsed = safeJsonInput<any>(gridData, undefined, 'remove-dormat.grid-data');
    if (parsed) {
      this.gridData = parsed;
      this.tryInit();
    }
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private removeDormatService: RemoveDormatService
  ) {}

  ngOnInit(): void {
    // Initial setup done via inputs
  }

  // Gate function to prevent duplicate initialization
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    // Check if ALL required data is present before initializing
    if (!this.globalService || !this.GENERAL_CONFIG || !this.gridData || this.gridData.length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init() {
    try {
      this.crudLoading = false;

      // Handle single object or array from gridData
      if (!Array.isArray(this.gridData)) {
        const obj: any = {};
        obj.row_id = (this.gridData as any).rowData?.object_id;
        obj.data = (this.gridData as any).rowData;
        this.gridData = [obj];
      }

      // Clone entities from gridData
      this.entities = [...this.gridData];
      console.log("entities", this.entities);

      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in init:', e);
      this.cdr.detectChanges();
    }
  }

  onConfirm() {
    this.removeDormat();
  }

  onCancel() {
    this.cancel();
  }

  removeDormat() {
    // Prevent double-click
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    this.btnDisabled = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const url = this.GENERAL_CONFIG.base_url + "/saveJsonObject?action_code=fdsa48";
    this.cc_keys = [];

    // Filter entities where TB_LOADED === 'N/A'
    this.entities.forEach((item: any) => {
      if (item.data && item.data.TB_LOADED === 'N/A') {
        this.cc_keys.push({ cc_key: (item.data.COMBINATION_KEY || item.data.CC_KEY) });
      }
    });

    const params = {
      tax_year: this.entities[0]?.data?.TAX_YEAR,
      cc_keys: JSON.stringify(this.cc_keys),
      status: 1,
      del_flag: 'Y'  // 'Y' for remove (different from add which uses 'N')
    };

    console.log("****cc_keys****", params);

    if (this.cc_keys && this.cc_keys.length > 0) {
      // Merge global params, filter params and specific params
      const mergedParams = {
        ...this.globalService?.globalParams,
        ...this.filterParams,
        ...params
      };

      this.removeDormatService.removeDormat(url, mergedParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          this.btnDisabled = false;
          this.crudLoading = false;

          if (response && response.callSuccess === "1") {
            this._snackBar.open('TB Inactive saved successfully', 'Close', {
              duration: 4000,
              panelClass: ['success-snackbar']
            });

            this.requestGridUpdate.emit('refresh');
          } else {
            this._snackBar.open(response?.errorMessage || 'Error occurred', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          this.btnDisabled = false;
          this.crudLoading = false;
          console.error('Error removing dormat:', error);
          this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      );
    } else {
      this._snackBar.open('Please select the N/A records', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      this.btnDisabled = false;
      this.crudLoading = false;
      this.cancel();
      this.cdr.detectChanges();
    }
  }

  removeEntity(index: number) {
    this.entities.splice(index, 1);
    if (this.entities.length === 0) {
      this.requestGridUpdate.emit('cancel');
    }
    this.cdr.detectChanges();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
